import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { animated, useTransition } from "@react-spring/web";
import type React from "react";
import { type RefObject, useEffect, useRef, useState } from "react";
import { tv } from "tailwind-variants";
import { cn } from "../../../util/classNameConstructor";
import Button, { type ButtonProps } from "../button/Button";
import Icon from "../icon/Icon";
import type { IconName } from "../icon/names";

const DropdownStyles = tv({
  slots: {
    content:
      "bg-surface-default border-border-default overflow-y-auto rounded-2xl border shadow-xl",
    separator: "border-border-subdued border-b my-1 -mx-1",
    dropdownItem:
      "focusable flex min-w-[144px] items-center gap-2 rounded-lg first:rounded-t-xl last:rounded-b-xl",
    arrowContainer:
      "[[data-side='bottom']_&]:top-0 [[data-side='top']_&]:bottom-0",
    arrowBorder: "fill-border-default translate-y-[0.5px] scale-150",
    arrowFill: "fill-surface-default -translate-y-[1px] scale-150",
  },
  variants: {
    full: { false: { dropdownItem: "max-w-xs" } },
    size: {
      xl: { dropdownItem: "h-14 px-6 py-4" },
      lg: { dropdownItem: "h-11 px-[1.125rem] py-[0.6875rem]" },
      md: { dropdownItem: "h-9 px-[0.875rem] py-2" },
      sm: { dropdownItem: "h-7 px-[0.625rem] py-1" },
    },
    hoverable: {
      true: {
        dropdownItem:
          "hover:bg-surface-hovered active:bg-surface-pressed cursor-pointer hover:outline-none hover:text-[var(--primary-1000)] active:text-[var(--primary-1000)]",
      },
      false: { dropdownItem: "cursor-default" },
    },
    active: { true: { dropdownItem: "bg-surface-pressed" } },
    selected: { true: { dropdownItem: "bg-surface-highlight-subdued" } },
    critical: {
      true: {
        dropdownItem:
          "text-text-critical hover:text-text-critical  active:text-text-critical hover:bg-surface-critical-subdued-hovered active:bg-surface-critical-subdued-pressed",
      },
    },
    disabled: {
      true: {
        dropdownItem:
          "text-text-subdued hover:bg-surface-default active:bg-surface-default cursor-default",
      },
    },
  },
  defaultVariants: { size: "sm" },
});

export interface DropdownProps extends Omit<ButtonProps, "children"> {
  items: {
    type?: "separator" | "label" | "item";
    text?: string;
    children?: React.ReactNode;
    disabled?: boolean;
    selected?: boolean;
    rightSlot?: React.ReactNode;
    leadingIcon?: IconName;
    leadingIconProps?: React.ComponentProps<typeof Icon>;
    critical?: boolean;
    className?: string;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    closeOnClick?: boolean;
    active?: boolean;
  }[];
  closeOnClick?: boolean;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  hasArrow?: boolean;
  portalContainer?: HTMLElement;
  Button?: React.ComponentType<ButtonProps>;
  full?: boolean;
  trigger?: React.ReactNode;
  classNames?: {
    content?: string;
    trigger?: string;
    dropdownItem?: string;
    separator?: string;
    arrowContainer?: string;
    arrowBorder?: string;
    arrowFill?: string;
  };
  asChild?: boolean;
  align?: "start" | "center" | "end";
  contentRef?: RefObject<HTMLElement>;
}

const Dropdown = ({
  id,
  contentRef,
  items,
  children,
  onOpenChange,
  classNames,
  full,
  trigger,
  portalContainer = document.body,
  hasArrow = true,
  modal = true,
  align = "center",
  side,
  Button: ButtonComponent = Button,
  open: propOpen,
  setOpen: setPropOpen,
  closeOnClick,
  asChild,
  ...props
}: DropdownProps) => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (propOpen !== undefined) {
      setOpen(propOpen);
    }
  }, [propOpen]);

  const {
    content,
    dropdownItem,
    separator,
    arrowContainer,
    arrowBorder,
    arrowFill,
  } = DropdownStyles({ size: props.size, full: !!full });
  const [open, setOpen] = useState(propOpen || false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const transitions = useTransition(open ? [1] : [], {
    from: { opacity: 0, scale: 0.9 },
    enter: { opacity: 1, scale: 1 },
    leave: { opacity: 0, scale: 0.9 },
    config: { tension: 1000, friction: 40 },
  });
  const modalStack = document.getElementById("modal-stack");

  return (
    <DropdownMenu.Root
      open={open}
      modal={modal}
      onOpenChange={(open) => {
        if (props.disabled) return;
        onOpenChange?.(open);
        setHasScrolled(false);
        setOpen(open);
        if (setPropOpen) {
          setPropOpen(open);
        }
      }}
    >
      <DropdownMenu.Trigger
        asChild={asChild}
        id={id}
        ref={ref}
        className={cn(classNames?.trigger)}
        aria-label={
          props["aria-label"] ||
          (typeof trigger === "string"
            ? trigger
            : typeof props.text === "string"
              ? props.text
              : typeof children === "string"
                ? children
                : undefined)
        }
      >
        {trigger || (
          <ButtonComponent
            trailingIcon="arrowhead-down-md"
            {...props}
            tag={asChild ? "button" : "span"}
            // onMouseEnter={() => setHover(true)}
            // onMouseLeave={() => setHover(false)}
          >
            {children}
          </ButtonComponent>
        )}
      </DropdownMenu.Trigger>
      {transitions((styles, item) =>
        items && item ? (
          <DropdownMenu.Portal
            forceMount
            container={
              modalStack?.contains(ref.current) ? modalStack : portalContainer
            }
          >
            <DropdownMenu.Content
              align={align}
              side={side}
              sideOffset={5}
              collisionPadding={10}
              className={cn("z-50", classNames?.contentWrapper)}
              ref={contentRef}
            >
              <animated.div
                style={{
                  ...styles,
                  maxHeight:
                    "minmax(calc(var(--radix-dropdown-menu-content-available-height) - 10px), 200px)",
                  transformOrigin:
                    "var(--radix-dropdown-menu-content-transform-origin)",
                  width: full ? "var(--radix-popper-anchor-width)" : "auto",
                }}
                className={cn(
                  content(),
                  items?.some((e) => e.text) ? "p-1" : "",
                  classNames?.content,
                )}
              >
                {items.map((e, i) => {
                  if (e.type === "separator")
                    return <DropdownMenu.Separator className={separator()} />;
                  if (e.type === "label")
                    return <DropdownMenu.Label>{e.text}</DropdownMenu.Label>;
                  if (e.text)
                    return (
                      <DropdownMenu.Item
                        key={i}
                        disabled={e.disabled}
                        ref={(ref) => {
                          if (!hasScrolled && e.active) {
                            ref?.scrollIntoView({ block: "center" });
                            setHasScrolled(true);
                          }
                        }}
                        className={cn(
                          dropdownItem({
                            active: e.active,
                            hoverable: !e.disabled && !!e.onClick,
                            critical: !!e.critical,
                            disabled: !!e.disabled,
                            selected: !!e.selected,
                            size: e?.size || props.size,
                          }),
                          classNames?.dropdownItem,
                          e.className,
                        )}
                        onClick={(event) => {
                          if (e.disabled) return;
                          e.onClick?.(event);
                          // if (e.closeOnClick) setOpen(false);
                        }}
                      >
                        {e.leadingIcon && (
                          <Icon
                            {...(e.leadingIconProps || {})}
                            name={e.leadingIcon}
                            aria-hidden="true"
                          />
                        )}
                        <span className="flex min-h-[20px] items-center">
                          {e.text}
                        </span>
                        {e.rightSlot && (
                          <div className="text-text-subdued ml-auto">
                            {e.rightSlot}
                          </div>
                        )}
                      </DropdownMenu.Item>
                    );
                  return (
                    <div
                      key={i}
                      onClick={
                        e.closeOnClick ? () => setOpen(false) : undefined
                      }
                    >
                      {e.children}
                    </div>
                  );
                })}
              </animated.div>

              {hasArrow && (
                <animated.div
                  style={{
                    opacity: styles.opacity,
                  }}
                  className={arrowContainer()}
                >
                  <DropdownMenu.Arrow className={arrowBorder()} />
                  <DropdownMenu.Arrow className={arrowFill()} />
                </animated.div>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        ) : null,
      )}
    </DropdownMenu.Root>
  );
};
Dropdown.displayName = "Dropdown";
export default Dropdown;
